import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { rulesApi } from '../services/api';
import { getCategoryIconUrl } from '../utils/images';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import ConfirmModal from '../components/ConfirmModal';
import './RulesAdmin.css';

function RulesAdmin() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [formData, setFormData] = useState({
    gr_cat_id: '',
    gr_desc: '',
    gr_body: '',
    gr_num: '',
    gr_sub: '0',
  });
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [allRules, setAllRules] = useState([]); // Store all loaded rules
  const [showDeletedOnly, setShowDeletedOnly] = useState(false); // Filter for soft-deleted rules
  const [showCategoriesAdmin, setShowCategoriesAdmin] = useState(false); // Show categories CRUD interface
  const [allCategories, setAllCategories] = useState([]); // All categories for admin
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showCategoryDeleteModal, setShowCategoryDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    icon: '',
  });
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);

  const loadCategories = useCallback(async () => {
    try {
      const response = await rulesApi.getCategories();
      
      // Handle both array and nested object responses
      let categories = [];
      if (Array.isArray(response.data)) {
        categories = response.data;
      } else if (response.data && response.data.data) {
        categories = response.data.data;
      }
      
      setCategories(categories);
      setAllCategories(categories); // Also store for categories admin
    } catch (err) {
      console.error('Error loading categories:', err);
      setCategories([]); // Set to empty array on error
      setAllCategories([]);
    }
  }, []);

  // Category Admin Handlers
  const handleCategoryCreate = () => {
    setEditingCategory(null);
    setCategoryFormData({ name: '', description: '', icon: '' });
    setIconFile(null);
    setIconPreview(null);
    setShowCategoryForm(true);
    setError('');
    setSuccess('');
  };

  const handleCategoryEdit = (category) => {
    setEditingCategory(category);
    setCategoryFormData({ 
      name: category.name || '', 
      description: category.description || '',
      icon: category.icon || ''
    });
    setIconFile(null);
    setIconPreview(category.icon ? getCategoryIconUrl(category.icon) : null);
    setShowCategoryForm(true);
    setError('');
    setSuccess('');
  };

  const handleCategoryDeleteClick = (category) => {
    setCategoryToDelete(category);
    setShowCategoryDeleteModal(true);
  };

  const handleCategoryDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    const id = categoryToDelete.cat_id || categoryToDelete.id;
    
    if (!id && id !== 0) {
      setError('Cannot delete category - ID not found');
      setShowCategoryDeleteModal(false);
      setCategoryToDelete(null);
      return;
    }
    
    try {
      setError('');
      setShowCategoryDeleteModal(false);
      await rulesApi.deleteCategory(id);
      setSuccess('Category deleted successfully');
      loadCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.response?.data?.message || 'Failed to delete category');
    } finally {
      setCategoryToDelete(null);
    }
  };

  const handleCategoryDeleteCancel = () => {
    setShowCategoryDeleteModal(false);
    setCategoryToDelete(null);
  };

  const handleCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // If a new icon file was selected, upload it first
      let iconFilename = categoryFormData.icon; // Keep existing icon if no new file
      
      console.log('Category submit - iconFile:', iconFile ? iconFile.name : 'none');
      console.log('Category submit - current icon:', categoryFormData.icon);
      
      if (iconFile) {
        console.log('Uploading icon file:', iconFile.name, 'Size:', iconFile.size, 'Type:', iconFile.type);
        try {
          const uploadResponse = await rulesApi.uploadCategoryIcon(iconFile);
          console.log('Icon upload response:', uploadResponse);
          console.log('Icon upload response data:', uploadResponse.data);
          
          if (uploadResponse.data && uploadResponse.data.success) {
            // Get filename from response
            iconFilename = uploadResponse.data.data?.filename || uploadResponse.data.data?.path || iconFilename;
            console.log('Icon uploaded successfully, filename:', iconFilename);
            console.log('Continuing to save category...');
          } else {
            console.error('Upload failed - response:', uploadResponse.data);
            throw new Error(uploadResponse.data?.message || 'Failed to upload icon');
          }
          // Clear the file after successful upload
          setIconFile(null);
          setIconPreview(null);
        } catch (uploadErr) {
          console.error('Icon upload error:', uploadErr);
          console.error('Upload error response:', uploadErr.response);
          throw new Error(uploadErr.response?.data?.message || uploadErr.message || 'Failed to upload icon');
        }
      }

      console.log('About to prepare category data. iconFilename:', iconFilename);
      console.log('categoryFormData:', categoryFormData);

      // Prepare category data with icon filename
      const categoryData = {
        ...categoryFormData,
        icon: iconFilename, // Use uploaded filename or existing icon
      };

      console.log('Saving category with data:', JSON.stringify(categoryData, null, 2));
      console.log('Icon filename being saved:', iconFilename);

      if (editingCategory) {
        // Update
        const id = editingCategory.cat_id || editingCategory.id;
        console.log('Updating category ID:', id);
        console.log('Update payload:', JSON.stringify(categoryData, null, 2));
        try {
          const updateResponse = await rulesApi.updateCategory(id, categoryData);
          console.log('Category update response:', updateResponse);
          console.log('Updated category data:', updateResponse.data);
          if (updateResponse.data && updateResponse.data.data) {
            console.log('Updated category icon:', updateResponse.data.data.icon);
          }
          setSuccess('Category updated successfully');
        } catch (updateErr) {
          console.error('Category update error:', updateErr);
          console.error('Update error response:', updateErr.response);
          console.error('Update error data:', updateErr.response?.data);
          throw new Error(updateErr.response?.data?.message || updateErr.message || 'Failed to update category');
        }
      } else {
        // Create
        console.log('Creating new category');
        console.log('Create payload:', JSON.stringify(categoryData, null, 2));
        try {
          const createResponse = await rulesApi.createCategory(categoryData);
          console.log('Category create response:', createResponse);
          setSuccess('Category created successfully');
        } catch (createErr) {
          console.error('Category create error:', createErr);
          console.error('Create error response:', createErr.response);
          throw new Error(createErr.response?.data?.message || createErr.message || 'Failed to create category');
        }
      }

      // Clear form state
      setEditingCategory(null);
      setCategoryFormData({ name: '', description: '', icon: '' });
      setIconFile(null);
      setIconPreview(null);
      setShowCategoryForm(false);
      loadCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryCancel = () => {
    setShowCategoryForm(false);
    setEditingCategory(null);
    setCategoryFormData({ name: '', description: '', icon: '' });
    setIconFile(null);
    setIconPreview(null);
    setError('');
    setSuccess('');
  };

  const handleIconFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIconFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setIconPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const formatRuleNumber = (rule) => {
    if (rule.formatted_number) return rule.formatted_number;
    if (rule.number) return rule.number;
    const num = rule.gr_num ?? 0;
    const sub = rule.gr_sub ?? 0;
    return `${num}.${sub}`;
  };

  const applySearchFilter = useCallback((rulesToFilter, term) => {
    console.log('applySearchFilter called:', { 
      rulesCount: rulesToFilter.length, 
      term: term
    }); // Debug
    
    if (!term || term.trim() === '') {
      setRules(rulesToFilter);
      return;
    }

    if (!rulesToFilter || rulesToFilter.length === 0) {
      console.log('No rules to filter'); // Debug
      setRules([]);
      return;
    }

    const lowerTerm = term.toLowerCase().trim();
    const filtered = rulesToFilter.filter(rule => {
      const desc = (rule.gr_desc || rule.description || '').toLowerCase();
      const body = (rule.gr_body || rule.body || '').toLowerCase();
      const ruleNum = formatRuleNumber(rule).toString();
      const ruleId = (rule.id || rule.gr_id || '').toString();
      const categoryName = (rule.category_name || rule.category?.name || '').toLowerCase();

      return desc.includes(lowerTerm) ||
             body.includes(lowerTerm) ||
             ruleNum.includes(lowerTerm) ||
             ruleId.includes(lowerTerm) ||
             categoryName.includes(lowerTerm);
    });

    console.log('Filtered rules:', filtered.length); // Debug
    setRules(filtered);
  }, []); // No dependencies - formatRuleNumber is defined before this

  const loadRules = useCallback(async (overrideSearchTerm = null, overrideShowDeleted = null) => {
    try {
      setLoading(true);
      setError('');
      
      // Use override values if provided, otherwise use state
      const currentSearchTerm = overrideSearchTerm !== null ? overrideSearchTerm : searchTerm;
      const currentShowDeleted = overrideShowDeleted !== null ? overrideShowDeleted : showDeletedOnly;
      
      // If searching, load ALL rules (not paginated) so search works across all rules
      // If showing deleted only, also load all deleted rules
      const shouldLoadAll = currentSearchTerm && currentSearchTerm.trim() !== '';
      const loadAllForFilter = shouldLoadAll || currentShowDeleted;
      const currentPage = loadAllForFilter ? 1 : page;
      const currentPerPage = loadAllForFilter ? 10000 : perPage; // Large number to get all when searching/filtering
      
      console.log('Loading rules - page:', currentPage, 'perPage:', currentPerPage, 'searching:', shouldLoadAll, 'deletedOnly:', currentShowDeleted); // Debug
      
      // Pass deleted filter to API: currentShowDeleted=true means show only deleted
      // When currentShowDeleted is true, call getDeleted() to get only deleted rules
      // When currentShowDeleted is false, call getAll() to get all non-deleted rules
      const response = currentShowDeleted 
        ? await rulesApi.getDeleted(currentPage, currentPerPage)
        : await rulesApi.getAll(currentPage, currentPerPage);
      console.log('Rules API response:', response); // Debug
      console.log('Response data:', response.data); // Debug
      
      // Handle different response structures
      let loadedRules = [];
      let totalCount = 0;
      if (response.data) {
        if (response.data.success) {
          // Laravel API structure: { success: true, data: [...], meta: {...} }
          loadedRules = response.data.data || [];
          totalCount = response.data.meta?.total || 0;
        } else if (Array.isArray(response.data)) {
          // Direct array response: [...]
          loadedRules = response.data;
          totalCount = response.data.length;
        } else if (response.data.data) {
          // Nested data: { data: [...], meta: {...} }
          loadedRules = Array.isArray(response.data.data) ? response.data.data : [];
          totalCount = response.data.meta?.total || loadedRules.length;
        } else {
          setError(response.data.message || 'Failed to load rules - invalid response structure');
          loadedRules = [];
        }
      } else {
        setError('Invalid API response - no data received');
        loadedRules = [];
      }
      
      // Store all loaded rules and apply search filter
      console.log('Storing loaded rules:', loadedRules.length, 'total in DB:', totalCount); // Debug
      
      // Store ALL rules (for search filtering)
      // If searching or showing deleted only, we just loaded all rules, so use that
      // If not searching, we only have current page, so accumulate or replace
      if (loadAllForFilter) {
        // When searching or filtering, we loaded all rules, so store them all
        setAllRules(loadedRules);
      } else {
        // When not searching, we only have current page
        // Store them for potential future search
        setAllRules(loadedRules);
      }
      
      // Update total (only when not searching/filtering, to preserve pagination info)
      if (!loadAllForFilter) {
        setTotal(totalCount);
      }
      
      // Don't apply search filter here - let the searchTerm effect handle it
      // When showing deleted only, the API already filtered to only deleted rules
      // When showing all, the API already filtered to only non-deleted rules
      // So we can just set the rules directly
      setRules(loadedRules);
    } catch (err) {
      console.error('Error loading rules:', err); // Debug
      console.error('Error response:', err.response); // Debug
      console.error('Error message:', err.message); // Debug
      const errorMsg = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || 'Failed to load rules. Check console for details.';
      setError(errorMsg);
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, showDeletedOnly]); // Removed searchTerm and applySearchFilter to prevent infinite loop

  useEffect(() => {
    if (!authLoading) {
      if (!user || user.profile !== 'admin') {
        navigate('/profile');
        return;
      }
      loadCategories();
      loadRules();
    }
  }, [user, authLoading, navigate, page, loadCategories, loadRules]);

  // Apply search filter when searchTerm changes (separate from loading)
  // Only filter if we already have rules loaded
  useEffect(() => {
    if (allRules.length > 0) {
      if (searchTerm && searchTerm.trim() !== '') {
        // Filter existing rules
        applySearchFilter(allRules, searchTerm);
      } else {
        // No search term - show all rules
        setRules(allRules);
      }
    }
    // Only depend on searchTerm and allRules - applySearchFilter is stable
  }, [searchTerm, allRules, applySearchFilter]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreate = () => {
    setEditingRule(null);
    setFormData({
      gr_cat_id: '',
      gr_desc: '',
      gr_body: '',
      gr_num: '',
      gr_sub: '0',
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleToggleDeletedFilter = () => {
    const newShowDeleted = !showDeletedOnly;
    setShowDeletedOnly(newShowDeleted);
    setSearchTerm(''); // Clear search when toggling deleted filter
    // Pass the new value directly to loadRules to avoid state timing issues
    setTimeout(() => {
      loadRules(null, newShowDeleted); // Pass newShowDeleted directly
    }, 0);
  };

  const handleRestore = async (rule) => {
    const id = (rule.gr_id !== undefined && rule.gr_id !== null) 
      ? rule.gr_id 
      : (rule.id !== undefined && rule.id !== null ? rule.id : null);
    
    if (!id && id !== 0) {
      console.error('No ID found in rule object:', rule);
      setError('Cannot restore rule - ID not found');
      return;
    }
    
    try {
      setError('');
      await rulesApi.restore(id);
      setSuccess('Rule restored successfully');
      // Reload rules with current showDeletedOnly state
      // When restoring, if viewing deleted rules, reload deleted list (so restored rule is removed)
      // If viewing all rules, reload all rules (so restored rule appears)
      loadRules(null, showDeletedOnly);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error restoring rule:', err);
      console.error('Error response:', err.response); // Debug
      setError(err.response?.data?.message || 'Failed to restore rule');
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      gr_cat_id: rule.gr_cat_id || rule.category?.id || '',
      gr_desc: rule.gr_desc || rule.description || '',
      gr_body: rule.gr_body || rule.body || '',
      gr_num: rule.gr_num || rule.number?.split('.')[0] || '',
      gr_sub: rule.gr_sub || rule.number?.split('.')[1] || '0',
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDeleteClick = (rule) => {
    setRuleToDelete(rule);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ruleToDelete) {
      console.error('No rule to delete - ruleToDelete is null/undefined');
      return;
    }

    console.log('Deleting rule:', ruleToDelete); // Debug
    // Use ?? (nullish coalescing) instead of || to handle 0 as a valid ID
    // If id is 0, || would treat it as falsy and skip to gr_id
    // Try gr_id first (the actual database field), then fall back to id
    const id = (ruleToDelete.gr_id !== undefined && ruleToDelete.gr_id !== null) 
      ? ruleToDelete.gr_id 
      : (ruleToDelete.id !== undefined && ruleToDelete.id !== null ? ruleToDelete.id : null);
    console.log('Extracted ID:', id, 'from rule:', { id: ruleToDelete.id, gr_id: ruleToDelete.gr_id }); // Debug
    
    // Allow 0 as a valid ID (check for null/undefined only)
    if (id === null || id === undefined) {
      console.error('No ID found in rule object:', ruleToDelete);
      setError('Cannot delete rule - ID not found');
      setShowDeleteModal(false);
      setRuleToDelete(null);
      return;
    }
    
    try {
      setError('');
      setShowDeleteModal(false);
      console.log('Calling rulesApi.delete with ID:', id); // Debug
      await rulesApi.delete(id);
      setSuccess('Rule deleted successfully (soft delete - recoverable)');
      // Reload rules with current showDeletedOnly state
      // When deleting, we're viewing all rules, so reload all rules (showDeletedOnly should be false)
      loadRules(null, showDeletedOnly);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting rule:', err);
      console.error('Error response:', err.response); // Debug
      setError(err.response?.data?.message || 'Failed to delete rule');
    } finally {
      setRuleToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setRuleToDelete(null);
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    const wasSearching = searchTerm && searchTerm.trim() !== '';
    const isSearching = term && term.trim() !== '';
    
    setSearchTerm(term);
    console.log('Search changed:', { term, allRulesLength: allRules.length, currentRulesLength: rules.length }); // Debug
    
    // If user just started searching (wasn't searching before, now is), load all rules
    // Otherwise, the search filter useEffect will handle filtering existing rules
    if (isSearching && !wasSearching) {
      loadRules(term);
    }
    
    // Reset to page 1 when searching
    if (isSearching && page !== 1) {
      setPage(1);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    // Restore all rules - use allRules if available, otherwise reload
    if (allRules.length > 0) {
      setRules(allRules);
    } else {
      // If allRules is empty, reload rules
      loadRules();
    }
    if (page !== 1) {
      setPage(1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const submitData = {
        gr_cat_id: formData.gr_cat_id.toString(), // Send as string (Laravel expects string)
        gr_desc: formData.gr_desc,
        gr_body: formData.gr_body,
      };

      // Add gr_num and gr_sub if provided
      if (formData.gr_num) {
        submitData.gr_num = parseInt(formData.gr_num);
      }
      if (formData.gr_sub) {
        submitData.gr_sub = parseInt(formData.gr_sub);
      }

      if (editingRule) {
        // Update
        const ruleId = editingRule.id || editingRule.gr_id;
        await rulesApi.update(ruleId, submitData);
        setSuccess('Rule updated successfully');
      } else {
        // Create
        await rulesApi.create(submitData);
        setSuccess('Rule created successfully');
      }

      setShowForm(false);
      // Reload rules with current showDeletedOnly state
      // After create/update, we want to show all rules (not deleted), so pass false
      // But respect current filter state if user is viewing deleted rules
      loadRules(null, showDeletedOnly);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving rule:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      
      // Handle validation errors (422)
      if (err.response?.status === 422 && err.response?.data?.errors) {
        const validationErrors = err.response.data.errors;
        const errorMessages = Object.entries(validationErrors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
        setError(`Validation errors:\n${errorMessages}`);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to save rule');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRule(null);
    setFormData({
      gr_cat_id: '',
      gr_desc: '',
      gr_body: '',
      gr_num: '',
      gr_sub: '0',
    });
    setError('');
    setSuccess('');
  };

  if (authLoading || !user) {
    return (
      <>
        <Header />
        <div className="container">
          <Loading />
        </div>
        <Footer />
      </>
    );
  }

  if (user.profile !== 'admin') {
    return null; // Will redirect
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className="rules-admin-page">
          <div className="admin-header">
            <div className="admin-header-top">
              <Link to="/admin" className="btn btn-secondary back-btn">
                <i className="bi bi-arrow-left"></i> Back to GR Admin
              </Link>
              <h1>Rules Admin</h1>
            </div>
            {!showForm && (
              <div className="admin-header-buttons">
                <button onClick={handleCreate} className="btn btn-primary">
                  <i className="bi bi-plus-circle"></i> Create New Rule
                </button>
                <button 
                  onClick={handleToggleDeletedFilter} 
                  className={`btn ${showDeletedOnly ? 'btn-warning' : 'btn-secondary'}`}
                  title={showDeletedOnly ? 'Show all rules' : 'Show deleted rules only'}
                >
                  <i className={`bi ${showDeletedOnly ? 'bi-arrow-counterclockwise' : 'bi-trash'}`}></i>
                  {showDeletedOnly ? ' Show All' : ' Show Deleted Only'}
                </button>
                <button 
                  onClick={() => setShowCategoriesAdmin(!showCategoriesAdmin)} 
                  className={`btn ${showCategoriesAdmin ? 'btn-warning' : 'btn-secondary'}`}
                  title={showCategoriesAdmin ? 'Hide categories admin' : 'Show categories admin'}
                >
                  <i className={`bi ${showCategoriesAdmin ? 'bi-x-circle' : 'bi-tags'}`}></i>
                  {showCategoriesAdmin ? ' Hide Categories' : ' Categories'}
                </button>
                <Link to="/admin/statuses" className="btn btn-secondary" title="Manage submission statuses (gr_status)">
                  <i className="bi bi-list-ul"></i> Statuses
                </Link>
              </div>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {showCategoriesAdmin ? (
            <div className="admin-form-container">
              <h2>Categories Admin</h2>
              
              {!showCategoryForm && (
                <div className="admin-header-buttons" style={{ marginBottom: '1rem' }}>
                  <button onClick={handleCategoryCreate} className="btn btn-primary">
                    <i className="bi bi-plus-circle"></i> Create New Category
                  </button>
                </div>
              )}

              {showCategoryForm ? (
                <form onSubmit={handleCategorySubmit} className="admin-form">
                  <div className="form-group">
                    <label htmlFor="name">Category Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={categoryFormData.name}
                      onChange={handleCategoryInputChange}
                      required
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={categoryFormData.description}
                      onChange={handleCategoryInputChange}
                      disabled={loading}
                      rows={4}
                      maxLength={1000}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="icon">Category Icon</label>
                    <input
                      type="file"
                      id="icon"
                      name="icon"
                      accept="image/gif,image/png,image/jpeg,image/jpg,image/svg+xml"
                      onChange={handleIconFileChange}
                      disabled={loading}
                    />
                    {iconPreview && (
                      <div className="icon-preview" style={{ marginTop: '0.5rem' }}>
                        <img 
                          src={iconPreview} 
                          alt="Icon preview" 
                          style={{ maxWidth: '64px', maxHeight: '64px', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                        <span style={{ marginLeft: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                          {iconFile ? iconFile.name : 'Current icon'}
                        </span>
                      </div>
                    )}
                    {categoryFormData.icon && !iconFile && !iconPreview && (
                      <div className="icon-preview" style={{ marginTop: '0.5rem' }}>
                        <img 
                          src={getCategoryIconUrl(categoryFormData.icon)} 
                          alt="Current icon" 
                          style={{ maxWidth: '64px', maxHeight: '64px', border: '1px solid #ccc', borderRadius: '4px' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <span style={{ marginLeft: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                          Current icon: {categoryFormData.icon}
                        </span>
                      </div>
                    )}
                    <small style={{ display: 'block', marginTop: '0.25rem', color: '#999' }}>
                      Upload a GIF, PNG, JPG, or SVG file (max 2MB). Icon will be stored in /images/ directory.
                    </small>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : (editingCategory ? 'Update Category' : 'Create Category')}
                    </button>
                    <button type="button" onClick={handleCategoryCancel} className="btn btn-secondary" disabled={loading}>
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="rules-admin-list">
                  {allCategories.length === 0 ? (
                    <p className="no-rules">No categories found</p>
                  ) : (
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Icon</th>
                          <th>Name</th>
                          <th>Description</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allCategories.map((category) => (
                          <tr key={category.cat_id || category.id}>
                            <td>{category.cat_id || category.id}</td>
                            <td>
                              {category.icon ? (
                                <img 
                                  src={getCategoryIconUrl(category.icon)} 
                                  alt={category.name}
                                  style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <span style={{ color: '#999' }}>-</span>
                              )}
                            </td>
                            <td>{category.name}</td>
                            <td className="description-cell">{category.description || '-'}</td>
                            <td className="actions-cell">
                              <button
                                onClick={() => handleCategoryEdit(category)}
                                className="btn btn-sm btn-edit"
                                title="Edit"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                onClick={() => handleCategoryDeleteClick(category)}
                                className="btn btn-sm btn-delete"
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ) : showForm ? (
            <div className="admin-form-container">
              <h2>{editingRule ? 'Edit Rule' : 'Create New Rule'}</h2>
              <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-group">
                  <label htmlFor="gr_cat_id">Category *</label>
                  <select
                    id="gr_cat_id"
                    name="gr_cat_id"
                    value={formData.gr_cat_id}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  >
                    <option value="">Select a category</option>
                    {Array.isArray(categories) && categories.map(cat => (
                      <option key={cat.cat_id || cat.id} value={cat.cat_id || cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="gr_desc">Description *</label>
                  <input
                    type="text"
                    id="gr_desc"
                    name="gr_desc"
                    value={formData.gr_desc}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    maxLength={255}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="gr_body">Body *</label>
                  <textarea
                    id="gr_body"
                    name="gr_body"
                    value={formData.gr_body}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    rows={6}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="gr_num">Rule Number</label>
                    <input
                      type="number"
                      id="gr_num"
                      name="gr_num"
                      value={formData.gr_num}
                      onChange={handleInputChange}
                      disabled={loading}
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="gr_sub">Sub Rule</label>
                    <input
                      type="number"
                      id="gr_sub"
                      name="gr_sub"
                      value={formData.gr_sub}
                      onChange={handleInputChange}
                      disabled={loading}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : (editingRule ? 'Update Rule' : 'Create Rule')}
                  </button>
                  <button type="button" onClick={handleCancel} className="btn btn-secondary" disabled={loading}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <>
              <div className="admin-search-container">
                <div className="admin-search">
                  <i className="bi bi-search"></i>
                  <input
                    type="text"
                    placeholder="Search rules by description, body, number, ID, or category..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="admin-search-input"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="admin-search-clear"
                      title="Clear search"
                    >
                      <i className="bi bi-x-circle"></i>
                    </button>
                  )}
                </div>
                {searchTerm && (
                  <div className="admin-search-results">
                    Showing {rules.length} of {allRules.length} rules
                  </div>
                )}
              </div>

              <div className="admin-stats">
                <p>Total Rules: {searchTerm ? allRules.length : total}</p>
                {!searchTerm && <p>Page {page} of {Math.ceil(total / perPage)}</p>}
              </div>

              {loading ? (
                <Loading />
              ) : (
                <div className="rules-admin-list">
                  {rules.length === 0 ? (
                    <p className="no-rules">No rules found</p>
                  ) : (
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Number</th>
                          <th>Category</th>
                          <th>Description</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rules.map((rule) => {
                          // Get category ID from rule - try multiple sources
                          let catId = rule.gr_cat_id || rule.category?.id || rule.category?.cat_id;
                          
                          // If gr_other1 is a number (like "100"), it's the category ID
                          if (!catId && rule.gr_other1) {
                            const grOther1Num = Number(rule.gr_other1);
                            if (!isNaN(grOther1Num)) {
                              catId = grOther1Num;
                            }
                          }
                          
                          // Always try to look up category name from allCategories list
                          let categoryName = null;
                          if (catId && allCategories.length > 0) {
                            const foundCategory = allCategories.find(cat => {
                              const catIdToMatch = cat.cat_id || cat.id;
                              return String(catIdToMatch) === String(catId);
                            });
                            if (foundCategory) {
                              categoryName = foundCategory.name;
                            }
                          }
                          
                          // Fallback to API response fields (but check if it's not just an ID)
                          if (!categoryName) {
                            const apiCategoryName = rule.category_name || rule.category?.name;
                            // Only use if it exists and doesn't look like a number
                            if (apiCategoryName && isNaN(apiCategoryName)) {
                              categoryName = apiCategoryName;
                            }
                          }
                          
                          // Final fallback - show ID if no name found
                          if (!categoryName && catId) {
                            categoryName = `Category ${catId}`;
                          }
                          
                          return (
                          <tr key={rule.id || rule.gr_id}>
                            <td>{rule.id || rule.gr_id}</td>
                            <td>#{formatRuleNumber(rule)}</td>
                            <td>{categoryName || 'N/A'}</td>
                            <td className="description-cell">{rule.gr_desc || rule.description || 'N/A'}</td>
                            <td className="actions-cell">
                              {!showDeletedOnly && (
                                <>
                                  <button
                                    onClick={() => handleEdit(rule)}
                                    className="btn btn-sm btn-edit"
                                    title="Edit"
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(rule)}
                                    className="btn btn-sm btn-delete"
                                    title="Delete"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </>
                              )}
                              {showDeletedOnly && (
                                <button
                                  onClick={() => handleRestore(rule)}
                                  className="btn btn-sm btn-restore"
                                  title="Restore (Undelete)"
                                >
                                  <i className="bi bi-arrow-counterclockwise"></i> Restore
                                </button>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}

                  {!searchTerm && total > perPage && (
                    <div className="pagination">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1 || loading}
                        className="btn btn-secondary"
                      >
                        Previous
                      </button>
                      <span>Page {page} of {Math.ceil(total / perPage)}</span>
                      <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={page >= Math.ceil(total / perPage) || loading}
                        className="btn btn-secondary"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
      <ConfirmModal
        show={showDeleteModal}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Delete Rule"
        message={ruleToDelete ? `Are you sure you want to delete Rule #${formatRuleNumber(ruleToDelete)}? This will soft delete the rule (it can be recovered).` : 'Are you sure you want to delete this rule?'}
        confirmText="Delete"
        cancelText="Cancel"
        confirmType="danger"
      />
      <ConfirmModal
        show={showCategoryDeleteModal}
        onConfirm={handleCategoryDeleteConfirm}
        onCancel={handleCategoryDeleteCancel}
        title="Delete Category"
        message={categoryToDelete ? `Are you sure you want to delete category "${categoryToDelete.name}"?` : 'Are you sure you want to delete this category?'}
        confirmText="Delete"
        cancelText="Cancel"
        confirmType="danger"
      />
    </>
  );
}

export default RulesAdmin;
